import React, { useState, useRef, useCallback } from 'react';
import { useUsers } from '../hooks/useUsers';
import UsersTable from '../components/users/UsersTable';
import UserForm from '../components/users/UserForm';
import UserViewModal from '../components/users/UserViewModal';
import UserWarehousesModal from '../components/users/UserWarehousesModal';
import UserActions from '../components/users/UserActions';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { AdminGuard } from '../components/RoleGuards';
import { useErrorDialog } from '../context/errorDialogContext';
import useAuth from '../hooks/useAuth';


/**
 * Users page
 * - Provides UI to list, create, edit, view, delete, bulk-delete, export and
 *   toggle status for users. Uses `useUsers` hook for data operations.
 */
const Users = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  // Custom hook for users data and operations
  const {
    users,
    loading,
    error,
    clearError,
    selectedUsers,
    currentPage,
    totalPages,
    totalCount,
    filters,
    sortConfig,
    fetchUsers,
    getUserById,
    createUser,
    updateUser,
    deleteUser,
    bulkDeleteUsers,
    toggleUserStatus,
    exportUsers,
    updateFilters,
    updateSort,
    goToPage,
    nextPage,
    prevPage,
    selectUser,
    selectAllUsers,
    clearSelection,
    isUserSelected,
    isAllSelected,
    hasUsers,
    hasSelection,
    selectionCount,
  } = useUsers();

  const filteredUsersList = React.useMemo(() => {
    return users.filter(u => !currentUser || u.id !== currentUser.id);
  }, [users, currentUser]);

  // Local state for UI interactions
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  const [showWarehousesModal, setShowWarehousesModal] = useState(false);
  const [viewingWarehousesUser, setViewingWarehousesUser] = useState(null);

  const strings = t('users', { returnObjects: true });
  const { showError } = useErrorDialog();

  /**
   * Global error watcher for user operations
   * - Displays errors via the global error dialog when `error` is set
   */
  React.useEffect(() => {
    if (error) {
      showError(error, strings.page?.alerts?.errorTitle || t('users.errors.errorTitle'));
      // Clear it as soon as it has been shown. The hook holds `error` until the next
      // request replaces it, and `strings.page` is a fresh object on every render
      // (t(..., { returnObjects: true }) does not memoize), so this effect re-runs on
      // every render — leaving `error` set re-opens the dialog the operator just
      // dismissed, which reads as a frozen screen.
      clearError();
    }
  }, [error, showError, clearError, strings.page]);

  /** Refresh the users list, forcing a server reload */
  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      await fetchUsers({ force: true });
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setRefreshLoading(false);
    }
  };

  /** Open user form for creating a new user */
  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  /** Open user form prefilled for editing the given userId */
  const handleEditUser = async (userId) => {
    if (currentUser && currentUser.id === userId) {
      window.dispatchEvent(new CustomEvent('navigate', { detail: { page: 'profile' } }));
      return;
    }
    setActionLoading(true);
    try {
      const user = await getUserById(userId, { withLoading: false });
      if (user) {
        setEditingUser(user);
        setShowUserForm(true);
      }
    } catch (err) {
      console.error('Failed to fetch user for editing:', err);
    } finally {
      setActionLoading(false);
    }
  };

  /** Open view modal and load user details for the provided userId */
  const handleViewUser = async (userId) => {
    setViewLoading(true);
    setViewingUser(null);
    setShowViewModal(true);
    try {
      const user = await getUserById(userId, { withLoading: false });
      if (user) {
        setViewingUser(user);
      }
    } catch (err) {
      console.error('Failed to fetch user for viewing:', err);
    } finally {
      setViewLoading(false);
    }
  };

  /** Open modal to view assigned warehouses for a user */
  const handleViewWarehouses = (user) => {
    setViewingWarehousesUser(user);
    setShowWarehousesModal(true);
  };

  /**
   * Prepare delete confirmation for a single user
   * - resolves display name for the confirm message and sets confirm state
   */
  const handleDeleteUser = (userId) => {
    const user = users.find((u) => u.id === userId) || null;
    const nameParts = [
      user?.first_name || user?.firstName,
      user?.last_name || user?.lastName,
    ].filter(Boolean);
    const displayName = (nameParts.join(' ').trim() || user?.username || strings.table.copy.unnamedUser).trim();

    setDeleteConfirm({
      type: 'single',
      user,
      userId,
      title: strings.confirm.deleteSingle.title,
      message: strings.confirm.deleteSingle.message.replace('{name}', displayName || strings.table.copy.unnamedUser),
      confirmLabel: strings.confirm.deleteSingle.confirmLabel,
      loadingLabel: strings.confirm.deleteSingle.loadingLabel,
      cancelLabel: strings.confirm.buttons.cancel,
      confirmVariant: 'danger',
    });
  };

  /**
   * Prepare delete confirmation for bulk selected users
   */
  const handleBulkDelete = () => {
    const message = selectionCount === 1
      ? strings.confirm.deleteBulk.messageSingle
      : strings.confirm.deleteBulk.messageMultiple.replace('{count}', selectionCount);

    setDeleteConfirm({
      type: 'bulk',
      count: selectionCount,
      title: strings.confirm.deleteBulk.title,
      message,
      confirmLabel: strings.confirm.deleteBulk.confirmLabel,
      loadingLabel: strings.confirm.deleteBulk.loadingLabel,
      cancelLabel: strings.confirm.buttons.cancel,
      confirmVariant: 'danger',
    });
  };

  /** Execute the confirmed delete (single or bulk) operation */
  const confirmDelete = async () => {
    setActionLoading(true);
    try {
      let result;

      if (deleteConfirm.type === 'single') {
        // Prefer explicit userId, fall back to user object if present
        const idToDelete = deleteConfirm.userId || deleteConfirm.user?.id;
        if (!idToDelete) {
          throw new Error(t('users.errors.noUserIdToDelete'));
        }
        result = await deleteUser(idToDelete);
      } else {
        result = await bulkDeleteUsers();
      }

      if (result.success) {
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error('Delete operation failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  /** Handle submit from the user form for create or update */
  const handleFormSubmit = async (userData) => {
    setActionLoading(true);
    try {
      let result;

      if (editingUser) {
        result = await updateUser(editingUser.id, userData);
      } else {
        result = await createUser(userData);
      }

      if (result.success) {
        setShowUserForm(false);
        setEditingUser(null);
      }

      return result;
    } catch (err) {
      console.error('Form submission failed:', err);
      return { success: false, error: 'Operation failed' };
    } finally {
      setActionLoading(false);
    }
  };

  /** Export users to CSV (delegates to hook) */
  const handleExportUsers = async (exportFilters = {}) => {
    setActionLoading(true);
    try {
      const params = {
        ...filters,
        ...exportFilters,
      };
      const result = await exportUsers(params);
      if (result.success) {
        const url = window.URL.createObjectURL(result.data);
        const a = document.createElement('a');
        a.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        const format = params.format || 'csv';
        a.download = `${strings.actions?.downloadPrefix || 'users_export_'}${timestamp}.${format === 'excel' ? 'xlsx' : 'csv'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        showError(result.error || t('users.errors.exportFailed'), t('users.errors.exportErrorTitle'));
      }
      return result;
    } catch (err) {
      console.error('Export failed:', err);
      showError(t('common.messages.unexpectedErrorDuringExport'), t('common.messages.exportError'));
      return { success: false, error: t('users.errors.exportFailed') };
    } finally {
      setActionLoading(false);
    }
  };

  /** Toggle active status for a user */
  const handleToggleStatus = async (userId, isActive) => {
    setActionLoading(true);
    try {
      await toggleUserStatus(userId, isActive);
    } catch (err) {
      console.error('Status toggle failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const searchDebounceRef = useRef(null);
  const [searchInputValue, setSearchInputValue] = useState(filters.search || '');

  /** Updates input immediately; debounces API call by 400ms */
  const handleSearch = useCallback((searchTerm) => {
    setSearchInputValue(searchTerm);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      updateFilters({ search: searchTerm });
    }, 400);
  }, [updateFilters]);

  return (
    <AdminGuard fallback={(
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold">{strings.page.accessDenied.title}</h2>
          <p className="text-gray-600">{strings.page.accessDenied.message}</p>
        </div>
      </div>
    )}>
      <div className="min-h-screen bg-gray-50 p-6">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{strings.page.header.title}</h1>
              <p className="mt-2 text-gray-600">
                {strings.page.header.subtitle}
              </p>
            </div>

            {/* Stats */}
            <div className="flex space-x-4">
              <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-gray-500">{strings.page.stats.total}</div>
                  <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
                </div>
              </div>
              {hasSelection && (
                <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-3">
                    <div className="text-sm text-blue-600">{strings.page.stats.selected}</div>
                    <div className="text-2xl font-bold text-blue-700">{selectionCount}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Action Bar */}
        <UserActions
          onAddUser={handleCreateUser}
          onDeleteSelected={handleBulkDelete}
          onExportCSV={handleExportUsers}
          onRefresh={handleRefresh}
          selectedUsers={selectedUsers}
          users={users}
          loading={actionLoading}
          refreshLoading={refreshLoading}
          searchValue={searchInputValue}
          onSearchChange={handleSearch}
          is_active={filters.is_active}
          onActiveFilterChange={(val) => updateFilters({ is_active: val })}
        />

        {/* Users Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
          <UsersTable
            users={filteredUsersList}
            loading={loading}
            selectedUsers={selectedUsers}
            sortConfig={sortConfig}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onSort={updateSort}
            onSelectUser={selectUser}
            onSelectAll={(checked) => selectAllUsers(checked, filteredUsersList.map(u => u.id))}  /* only the DISPLAYED rows, never the signed-in admin */
            onClearSelection={clearSelection}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            onViewUser={handleViewUser}
            onViewWarehouses={handleViewWarehouses}
            onToggleStatus={handleToggleStatus}
            onPageChange={goToPage}
            onNextPage={nextPage}
            onPrevPage={prevPage}
            isUserSelected={isUserSelected}
            isAllSelected={isAllSelected}
            hasUsers={hasUsers}
            actionLoading={actionLoading}
            viewLoading={viewLoading}
          />
        </div>

        {totalPages > 1 && (
          <div
            className="mt-4 flex items-center justify-between rounded-xl border px-6 py-3"
            style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}
          >
            <span className="text-sm" style={{ color: '#6b7280' }}>
              Page {currentPage} of {totalPages} — {totalCount} total users
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={prevPage}
                disabled={currentPage <= 1 || loading}
                className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6b7280' }}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                  acc.push(p);
                  return acc;
                }, [])
                .map((item) =>
                  typeof item === 'string' ? (
                    <span key={item} className="px-2 text-sm" style={{ color: '#6b7280' }}>…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => goToPage(item)}
                      disabled={loading}
                      className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: item === currentPage ? '#0066cc' : 'transparent',
                        color: item === currentPage ? '#fff' : '#374151',
                        border: item === currentPage ? 'none' : '1px solid rgba(0,0,0,0.15)',
                      }}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                type="button"
                onClick={nextPage}
                disabled={currentPage >= totalPages || loading}
                className="rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: '#0066cc', color: '#fff' }}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* User Form Modal */}
        {showUserForm && (
          <UserForm
            user={editingUser}
            isOpen={showUserForm}
            onClose={() => {
              setShowUserForm(false);
              setEditingUser(null);
            }}
            onSubmit={handleFormSubmit}
            loading={actionLoading}
          />
        )}

        {/* User View Modal */}
        {showViewModal && (
          <UserViewModal
            isOpen={showViewModal}
            onClose={() => {
              setShowViewModal(false);
              setViewingUser(null);
            }}
            user={viewingUser}
            loading={viewLoading}
          />
        )}

        {/* User Warehouses Modal */}
        {showWarehousesModal && (
          <UserWarehousesModal
            isOpen={showWarehousesModal}
            onClose={() => {
              setShowWarehousesModal(false);
              setViewingWarehousesUser(null);
            }}
            warehouses={viewingWarehousesUser?.warehouses || []}
          />
        )}

        {deleteConfirm && (
          <ConfirmDialog
            isOpen
            title={deleteConfirm.title}
            message={deleteConfirm.message}
            confirmLabel={deleteConfirm.confirmLabel}
            cancelLabel={deleteConfirm.cancelLabel}
            confirmVariant={deleteConfirm.confirmVariant}
            loading={actionLoading}
            loadingLabel={deleteConfirm.loadingLabel}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteConfirm(null)}
          />
        )}
      </div>
    </AdminGuard>
  );
};

export default Users;
